'use client';


import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import StatusNotificationBadge from './StatusNotificationBadge';

type UserRole = 'broker' | 'admin';

interface RoleBasedNavigationProps {
  userRole?: UserRole;
  notificationCounts?: Record<string, number>;
  isCollapsed?: boolean;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  roles: UserRole[];
  description: string;
  notificationKey?: string;
  badge?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/broker-dashboard',
    icon: 'HomeIcon',
    roles: ['broker'],
    description: 'Case overview and activity summary',
  },
  {
    label: 'My Cases',
    path: '/case-detail-management',
    icon: 'DocumentTextIcon',
    roles: ['broker'],
    description: 'Manage and track your pension cases',
    notificationKey: 'cases',
  },
  {
    label: 'New Case',
    path: '/case-creation-wizard',
    icon: 'PlusCircleIcon',
    roles: ['broker'],
    description: 'Start a new pension case application',
    badge: 'Action',
  },
  {
    label: 'Control Center',
    path: '/admin-control-center',
    icon: 'AdjustmentsHorizontalIcon',
    roles: ['admin'],
    description: 'System-wide oversight and analytics',
    notificationKey: 'admin',
  },
  {
    label: 'Brokerages',
    path: '/brokerage-management',
    icon: 'BuildingOfficeIcon',
    roles: ['admin'],
    description: 'Manage brokerage accounts and permissions',
  },
  {
    label: 'Reports & Analytics',
    path: '/admin-reports-analytics',
    icon: 'ChartBarIcon',
    roles: ['admin'],
    description: 'Analytics, performance reports and case insights',
  },
];

const RoleBasedNavigation = ({
  userRole = 'broker',
  notificationCounts = { cases: 3, admin: 7 },
  isCollapsed = false,
}: RoleBasedNavigationProps) => {
  const pathname = usePathname();

  const filteredItems = NAV_ITEMS.filter((item) => item.roles.includes(userRole));

  const isActive = (path: string) => pathname === path;

  return (
    <nav
      className="w-full bg-card border-b border-border"
      aria-label="Role-based navigation"
      role="navigation"
    >
      <div className="max-w-screen-2xl mx-auto px-6">
        <ul className="flex items-center gap-1 h-12" role="list">
          {filteredItems.map((item) => {
            const notifCount = item.notificationKey
              ? notificationCounts[item.notificationKey] ?? 0
              : 0;
            const active = isActive(item.path);

            return (
              <li key={`${item.path}-${item.label}`} role="listitem">
                <Link
                  href={item.path}
                  title={item.description}
                  className={`relative flex items-center gap-2 px-4 h-12 text-sm font-medium transition-all duration-[250ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-sm
                    ${active
                      ? 'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                    }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon
                    name={item.icon as any}
                    size={17}
                    variant={active ? 'solid' : 'outline'}
                    className={active ? 'text-primary' : 'text-text-secondary'}
                  />
                  <span className="hidden sm:inline">{item.label}</span>
                  {notifCount > 0 && (
                    <StatusNotificationBadge
                      count={notifCount}
                      size="sm"
                      variant={item.notificationKey === 'admin' ? 'warning' : 'default'}
                    />
                  )}
                  {item.badge && !notifCount && (
                    <span className="hidden md:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-caption font-500 bg-accent/10 text-accent">
                      {item.badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}

          {/* Role indicator */}
          <li className="ml-auto flex items-center" role="listitem">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-caption ${
              userRole === 'admin' ?'bg-primary/8 text-primary' :'bg-secondary/8 text-secondary'
            }`}>
              <Icon
                name={userRole === 'admin' ? 'ShieldCheckIcon' : 'BriefcaseIcon'}
                size={13}
                variant="solid"
              />
              <span className="hidden sm:inline">
                {userRole === 'admin' ? 'Admin View' : 'Broker View'}
              </span>
            </div>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default RoleBasedNavigation;